// liveHighlight.js — the live console's code as coloured HTML.
// -----------------------------------------------
// The editor is a plain textarea with transparent text; this renders the
// same text, character for character, into the layer behind it. Each line
// is its own span so a line can carry a backdrop (readable over a projected
// score) and flash when it runs. Nothing here may add or drop a character,
// or the caret drifts away from the text it sits in.

/** Text made safe to put into HTML. */
export const esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// One pass over a line: strings, comments, names before ( or :, numbers.
const TOKEN = /("(?:[^"\\]|\\.)*"?|'(?:[^'\\]|\\.)*'?)|(\/\/.*$)|([A-Za-z_][\w]*)(?=\s*\()|([A-Za-z_][\w]*)(?=\s*:)|(-?\d+(?:\.\d+)?)/g;

// a line's tokens, kept by its text: a key re-colours the line it is on, not the rest
const _lineCache = new Map();
function highlightLine(line) {
  let html = _lineCache.get(line);
  if (html == null) {
    if (_lineCache.size > 5000) _lineCache.clear();
    html = tokensOf(line);
    _lineCache.set(line, html);
  }
  return html;
}

function tokensOf(line) {
  if (/^\s*#/.test(line)) return `<span class="lc-tok-comment">${esc(line)}</span>`;
  let out = "";
  let last = 0;
  for (const m of line.matchAll(TOKEN)) {
    out += esc(line.slice(last, m.index));
    const cls = m[1] ? "str" : m[2] ? "comment" : m[3] ? "fn" : m[4] ? "key" : "num";
    out += `<span class="lc-tok-${cls}">${esc(m[0])}</span>`;
    last = m.index + m[0].length;
  }
  return out + esc(line.slice(last));
}

/**
 * Put `insert` (markup with no text of its own) into `html` just before
 * text character number `at`, counting an entity as the one character it
 * stands for and skipping tags. It goes after any tags already there, or
 * with `beforeTags` before them (a closing tag must, to nest).
 */
function insertAtText(html, at, insert, beforeTags = false) {
  let n = 0;
  for (let i = 0; i < html.length; i++) {
    if (beforeTags && n === at) return html.slice(0, i) + insert + html.slice(i);
    if (html[i] === "<") { i = html.indexOf(">", i); continue; }
    if (n === at) return html.slice(0, i) + insert + html.slice(i);
    if (html[i] === "&") i = html.indexOf(";", i);
    n++;
  }
  return html + insert;
}

const OPENS = "([{", CLOSES = ")]}";

/**
 * The bracket at the caret and its partner, as text positions [open, close],
 * or null. The caret is on a bracket when the character after it is one, or
 * else the one before it (just typed). Brackets in quotes and comments do
 * not count; a ( closed by ] matches nothing.
 */
export function matchBracket(text, pos) {
  const pair = new Map();
  const stack = [];
  let quote = "", comment = false, lineStart = true;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "\n") { comment = false; quote = ""; lineStart = true; continue; }
    if (comment) continue;
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (lineStart && ch === "#") { comment = true; continue; }
    if (ch !== " " && ch !== "\t") lineStart = false;
    if (ch === "/" && text[i + 1] === "/") { comment = true; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (OPENS.includes(ch)) stack.push(i);
    else if (CLOSES.includes(ch)) {
      const o = stack.pop();
      if (o != null && OPENS.indexOf(text[o]) === CLOSES.indexOf(ch)) { pair.set(o, i); pair.set(i, o); }
      else if (o != null) stack.length = 0;          // a mismatch: nothing beyond it is reliable
    }
  }
  for (const at of [pos, pos - 1]) {
    if (pair.has(at)) return [Math.min(at, pair.get(at)), Math.max(at, pair.get(at))];
  }
  return null;
}

/** Wrap text character `col` of a line's markup in a span of class `cls`. */
function wrapChar(html, col, cls) {
  return insertAtText(insertAtText(html, col + 1, "</span>", true), col, `<span class="${cls}">`);
}

/**
 * The text as HTML: highlightLines joined by the newlines between them, and
 * a space after a last line that is empty (highlightTail).
 */
export function highlightCode(text, flash = new Map(), caret = null, folded = new Map(), states = new Map()) {
  return highlightLines(text, flash, caret, folded, states).join("\n") + highlightTail(text);
}

/** A textarea ending in a newline shows an empty last line; a <pre> needs something on it to be as tall. */
export const highlightTail = text => (text.endsWith("\n") ? " " : "");

/**
 * The text as HTML, one `.lc-line` span a line. `flash` maps a line index
 * to "ok" or "err" for lines that have just run. `caret` ({ pos, focused })
 * draws the console's own caret -- the textarea's is a hairline no width
 * can be given to, too easy to lose on a projected screen -- and marks the
 * line it is on, and the bracket pair it is at. `folded` maps a #$ head's
 * line to the number of lines folded away under it.
 */
export function highlightLines(text, flash = new Map(), caret = null, folded = new Map(), states = new Map()) {
  const lines = text.split("\n");
  let caretLine = -1, caretCol = 0;
  const marks = new Map();                // line -> columns of the matched brackets
  if (caret) {
    const before = text.slice(0, caret.pos).split("\n");
    caretLine = before.length - 1;
    caretCol = before[before.length - 1].length;
    for (const at of matchBracket(text, caret.pos) || []) {
      const upTo = text.slice(0, at).split("\n");
      const line = upTo.length - 1;
      marks.set(line, [...(marks.get(line) || []), upTo[line].length]);
    }
  }
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const f = flash.get(i);
    const hidden = folded.get(i);
    const cls = "lc-line" + (lines[i].trim() ? "" : " lc-blank") + (f ? ` lc-flash-${f}` : "")
      + (i === caretLine ? " lc-current" : "")
      + (/^\s*#\$/.test(lines[i]) ? " lc-fold-head" : "") + (hidden ? " lc-folded" : "")
      + (states.get(i)?.state || []).map(st => ` lc-${st}`).join("");
    // a folded block: how much is tucked away, drawn by CSS after the head's
    // text (an attribute, no text of the layer's own)
    const foldAttr = hidden ? ` data-fold="${hidden} line${hidden === 1 ? "" : "s"}"` : "";
    let body = highlightLine(lines[i]);
    // the bracket at the caret and its partner (right to left: a wrap adds no text)
    for (const col of (marks.get(i) || []).sort((a, b) => b - a)) body = wrapChar(body, col, "lc-bracket");
    if (i === caretLine) {
      // the suggestion shows as ghost text after the caret: drawn by CSS from
      // an attribute, so it is no text of the layer's own (clicks still map)
      const ghost = caret.ghost ? `<span class="lc-ghost" data-ghost="${esc(caret.ghost).replace(/"/g, "&quot;")}"></span>` : "";
      body = insertAtText(body, caretCol, `<span class="lc-caret${caret.focused ? "" : " lc-caret-idle"}"></span>${ghost}`);
      // the reminder of what can go here: at the end of the line, out of the
      // flow (CSS: absolute), so it can neither wrap nor push text along
      if (caret.hint) body += `<span class="lc-hint" data-hint="${esc(caret.hint).replace(/"/g, "&quot;")}"></span>`;
    }
    // a sound paused or held: its tag, after the line that names it (CSS)
    const tag = states.get(i)?.tag;
    if (tag) body += `<span class="lc-state" data-state="${esc(tag)}"></span>`;
    out.push(`<span class="${cls}"${foldAttr}>${body}</span>`);
  }
  return out;
}
